import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Calculator, 
  ArrowRight,
  CheckCircle2,
  UserPlus,
  Volume2,
  VolumeX,
  FileText,
  Download,
  Database,
  ShieldCheck,
  XCircle,
  HelpCircle,
  TreePine,
  Home as HomeIcon,
  Users,
  FileQuestion,
  Map,
  Eye
} from 'lucide-react';
import { homeTranslations } from '../lib/homeTranslations';
import { AUTH_ENABLED, SALES_ENABLED } from '../lib/featureFlags';

import { API_BASE_URL } from '../lib/apiBase';

const API_URL = API_BASE_URL;

// Local no-op stubs (Analytics removed from this file; kept as safe no-ops
// so existing onClick handlers keep working without runtime errors).
const trackStartFirstCheck = () => {};

const trackBeslutsbrevInfoClick = () => {};

const Home = () => {
  const { language } = useLanguage();
  const { isAuthenticated, token } = useAuth();
  const [isMuted, setIsMuted] = useState(true);
  const [pdfCount, setPdfCount] = useState(0);
  const iframeRef = useRef(null);

  const t = homeTranslations[language] || homeTranslations.sv;

  // Fetch PDF count for logged-in users
  useEffect(() => {
    const fetchPdfCount = async () => {
      if (!isAuthenticated || !token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/beslutslage/my-cases`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const cases = await response.json();
          const paidCases = cases.filter(c => c.premium_unlocked);
          setPdfCount(paidCases.length);
        }
      } catch (err) {
        console.log('Could not fetch PDF count');
      }
    };
    
    fetchPdfCount();
  }, [isAuthenticated, token]);

  const toggleSound = () => {
    setIsMuted(!isMuted);
  };

  // Icons for situation cards
  const situationIcons = {
    heritage: TreePine,
    buy: HomeIcon,
    sell: FileText,
    harvest: Calculator,
    plan: Map,
    owners: Users,
    distance: Eye
  };

  return (
    <div className="space-y-16" data-testid="home-page">
      {/* 1. Hero Section - Calm, Swedish, Orientation-focused */}
      <section className="text-center py-12 md:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Trust Badge */}
          {t.hero.trustBadge && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-forest-50 border border-forest-200 rounded-full text-sm text-forest-700">
              <ShieldCheck className="h-4 w-4 text-forest-600" />
              <span>{t.hero.trustBadge}</span>
            </div>
          )}
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-forest-800 leading-tight" data-testid="hero-title">
            {t.hero.title}
          </h1>
          <p className="text-lg md:text-xl text-forest-700 max-w-3xl mx-auto">
            {t.hero.subtitle}
          </p>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            {t.hero.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            {isAuthenticated ? (
              <Link to="/verktyg" onClick={() => { trackStartFirstCheck(); }}>
                <Button size="lg" className="w-full sm:w-auto bg-forest-700 hover:bg-forest-800" data-testid="go-calculators-btn">
                  <Calculator className="mr-2 h-5 w-5" />
                  {t.goToCalculators}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/verktyg" onClick={() => { trackStartFirstCheck(); }}>
                  <Button size="lg" className="w-full sm:w-auto bg-forest-700 hover:bg-forest-800" data-testid="view-calculators-btn">
                    {t.hero.primaryBtn}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                {SALES_ENABLED && (
                  <a href="#beslutsbrev-section" onClick={() => trackBeslutsbrevInfoClick()}>
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-forest-300 text-forest-700 hover:bg-forest-50" data-testid="beslutsbrev-info-btn">
                      {t.hero.secondaryBtn}
                    </Button>
                  </a>
                )}
              </>
            )}
          </div>

          {/* Value Points - Updated to match new messaging */}
          {t.hero.stats && (
            <div className="flex flex-wrap justify-center gap-6 md:gap-10 pt-6 text-sm text-forest-700">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-forest-600" />
                <span>{t.hero.stats.first}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-forest-600" />
                <span>{t.hero.stats.second}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-forest-600" />
                <span>{t.hero.stats.third}</span>
              </div>
            </div>
          )}

          {/* Access Logic - Simplified */}
          {!isAuthenticated && (
            <div className="pt-4 max-w-md mx-auto">
              <p className="text-sm text-forest-600">
                {t.hero.accessLogic}
              </p>
            </div>
          )}

          {/* Wartemodus-Hinweis (dezent, einmalig, kein Banner) */}
          {!SALES_ENABLED && !AUTH_ENABLED && (
            <p
              className="pt-2 text-xs text-forest-500 max-w-lg mx-auto"
              data-testid="maintenance-hero-note"
            >
              Beslutsbrev och kontofunktioner är tillfälligt pausade. Kontakta{' '}
              <a
                href="mailto:info@hodema.se"
                className="underline hover:text-forest-700"
              >
                info@hodema.se
              </a>
              {' '}om du vill veta mer.
            </p>
          )}
        </div>
      </section>

      {/* Beslutsbrev Box - Only for logged-in users with PDFs */}
      {isAuthenticated && pdfCount > 0 && (
        <section className="max-w-2xl mx-auto -mt-8 mb-8 px-4">
          <Card className="border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg">
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <FileText className="h-6 w-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800">
                      {language === 'sv' ? 'Dina Beslutsbrev' : 'Your Decision Letters'}
                    </h3>
                    <p className="text-sm text-green-600">
                      {language === 'sv' 
                        ? `Du har ${pdfCount} beslutsbrev tillgängliga för nedladdning`
                        : `You have ${pdfCount} decision letters available for download`
                      }
                    </p>
                  </div>
                </div>
                <Link to="/mina-arenden?filter=pdf">
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    <Download className="mr-2 h-4 w-4" />
                    {language === 'sv' ? 'Visa & Ladda ner' : 'View & Download'}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* 2. Situations Section - "För dig som står inför en skoglig fråga" */}
      {t.situations && (
        <section className="py-8" data-testid="situations-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-forest-800 text-center mb-8">
              {t.situations.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {t.situations.items.map((item, index) => {
                const IconComponent = situationIcons[item.icon] || HelpCircle;
                return (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-4 bg-forest-50/50 border border-forest-100 rounded-lg hover:bg-forest-50 transition-colors"
                  >
                    <IconComponent className="h-5 w-5 text-forest-600 flex-shrink-0" />
                    <span className="text-forest-800 text-sm">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 2b. Distance Challenge Section - Why distance ownership is harder */}
      {t.distanceChallenge && (
        <section className="py-6 max-w-3xl mx-auto" data-testid="distance-challenge-section">
          <Card className="border-forest-200 bg-gradient-to-br from-forest-50/30 to-white">
            <CardContent className="p-6 md:p-8">
              <h3 className="text-lg font-semibold text-forest-800 mb-3">
                {t.distanceChallenge.title}
              </h3>
              <p className="text-forest-700 text-sm leading-relaxed mb-4">
                {t.distanceChallenge.text}
              </p>
              <p className="text-sm text-forest-600 italic">
                {t.distanceChallenge.note}
              </p>
            </CardContent>
          </Card>
        </section>
      )}

      {/* 3. Tiers Section — hidden in maintenance mode (Premium/Konto-Spalten irrelevant) */}
      {SALES_ENABLED && AUTH_ENABLED && t.tiers && (
        <section className="py-8" data-testid="tiers-section">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-forest-800 mb-3">
                {t.tiers.title}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t.tiers.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Tier 1: Gratis */}
              <Card className="border-forest-200 bg-gradient-to-b from-forest-50/50 to-white" data-testid="tier-free">
                <CardContent className="p-6 space-y-4">
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                    Gratis
                  </span>
                  <h3 className="text-lg font-semibold text-forest-800">
                    {t.tiers.free.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t.tiers.free.description}
                  </p>
                  <Link to="/verktyg">
                    <Button variant="outline" size="sm" className="w-full border-forest-300 text-forest-700 hover:bg-forest-50">
                      {t.tiers.free.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Tier 2: Registrerad */}
              <Card className="border-forest-200 bg-gradient-to-b from-slate-50/50 to-white" data-testid="tier-registered">
                <CardContent className="p-6 space-y-4">
                  <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded">
                    Konto
                  </span>
                  <h3 className="text-lg font-semibold text-forest-800">
                    {t.tiers.registered.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t.tiers.registered.description}
                  </p>
                  {!isAuthenticated ? (
                    AUTH_ENABLED ? (
                      <Link to="/register">
                        <Button variant="outline" size="sm" className="w-full border-forest-300 text-forest-700 hover:bg-forest-50">
                          <UserPlus className="mr-2 h-4 w-4" />
                          {t.tiers.registered.cta}
                        </Button>
                      </Link>
                    ) : null
                  ) : (
                    <Link to="/mina-arenden">
                      <Button variant="outline" size="sm" className="w-full border-forest-300 text-forest-700 hover:bg-forest-50">
                        Mina genomgångar
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>

              {/* Tier 3: Beslutsbrev */}
              <Card className="border-forest-200 bg-gradient-to-b from-amber-50/30 to-white" data-testid="tier-premium">
                <CardContent className="p-6 space-y-4">
                  <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                    {t.tiers.premium.badge}
                  </span>
                  <h3 className="text-lg font-semibold text-forest-800">
                    {t.tiers.premium.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t.tiers.premium.description}
                  </p>
                  <a href="#beslutsbrev-section">
                    <Button variant="outline" size="sm" className="w-full border-amber-300 text-amber-700 hover:bg-amber-50">
                      {t.tiers.premium.cta}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              {t.tiers.hint}
            </p>
          </div>
        </section>
      )}

      {/* 4. Featured Calculators Section */}
      <section className="space-y-6" data-testid="featured-calculators">
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-forest-800">
            {t.featuredCalcs.title}
          </h2>
          <p className="text-muted-foreground mt-2">{t.featuredCalcs.subtitle}</p>
        </div>
        
        {/* Primary Calculator - Highlighted */}
        {t.featuredCalcs.calculators.filter(c => c.primary).map((calc) => (
          <Link key={calc.id} to={`/verktyg?calc=fastighetsvarde`} className="block group max-w-md mx-auto">
            <Card className="border-2 border-forest-300 bg-gradient-to-br from-forest-50 to-white hover:shadow-lg transition-all">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-forest-200 group-hover:bg-forest-300 transition-colors">
                    <calc.icon className="h-6 w-6 text-forest-700" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-forest-600 mb-1">{t.featuredCalcs.recommended}</p>
                    <h3 className="font-bold text-forest-800 text-lg">{calc.name}</h3>
                    <p className="text-sm text-forest-600">{calc.desc}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-forest-400 group-hover:text-forest-600 group-hover:translate-x-1 transition-all" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}

        {/* Secondary Calculators - Grid with direct links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
          {t.featuredCalcs.calculators.filter(c => !c.primary).map((calc) => {
            const calcRoutes = {
              'value': 'virkesvarde',
              'volume': 'virkesvolym',
              'growth': 'tillvaxt',
              'co2': 'co2'
            };
            return (
              <Link 
                key={calc.id} 
                to={`/verktyg?calc=${calcRoutes[calc.id] || calc.id}`} 
                className="block group"
              >
                <Card className="h-full hover:border-forest-200 hover:shadow-sm transition-all">
                  <CardContent className="p-4 text-center">
                    <div className="p-2 rounded-full bg-forest-100 w-fit mx-auto mb-2 group-hover:bg-forest-200 transition-colors">
                      <calc.icon className="h-4 w-4 text-forest-600" />
                    </div>
                    <h3 className="font-medium text-forest-800 text-sm leading-tight">{calc.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{calc.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 4. Trust & Data Basis Section - Early credibility */}
      {t.trust && (
        <section className="max-w-4xl mx-auto" data-testid="trust-section">
          <div className="bg-gradient-to-br from-slate-50 to-stone-50 border border-slate-200 rounded-2xl p-6 md:p-8">
            <div className="text-center mb-6">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                {t.trust.label}
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-slate-800">
                {t.trust.title}
              </h3>
              <p className="text-slate-600 mt-2 max-w-2xl mx-auto">
                {t.trust.description}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {t.trust.sources.map((source, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/70 rounded-lg p-4">
                  <Database className="h-5 w-5 text-forest-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{source.name}</p>
                    <p className="text-xs text-slate-500">{source.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex items-start gap-3 bg-amber-50/70 border border-amber-100 rounded-lg p-4 mb-4">
              <ShieldCheck className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {t.trust.note}
              </p>
            </div>

            {/* Link to methodology page */}
            <div className="text-center">
              <Link 
                to="/metodik" 
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-forest-700 transition-colors"
              >
                {t.trust.moreLink || 'Mer om metodik och datakällor'}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* 5. Boundaries Section - "Vad Skogskalkyl inte är" */}
      {t.boundaries && (
        <section className="max-w-3xl mx-auto" data-testid="boundaries-section">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-slate-500" />
              {t.boundaries.title}
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
              {t.boundaries.items.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="bg-forest-50 border border-forest-200 rounded-lg p-4">
              <p className="text-sm text-forest-800">
                {t.boundaries.positive}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 6. Beslutsbrev Section — hidden in maintenance mode */}
      {SALES_ENABLED && t.beslutsbrev && (
        <section id="beslutsbrev-section" className="max-w-3xl mx-auto scroll-mt-20" data-testid="beslutsbrev-section">
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-6">
                <span className="px-3 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  {t.beslutsbrev.price}
                </span>
                <h3 className="text-xl font-semibold text-forest-800 mt-3">
                  {t.beslutsbrev.title}
                </h3>
                <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
                  {t.beslutsbrev.description}
                </p>
              </div>

              <div className="bg-white/70 border border-slate-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-slate-800 mb-3 text-sm">
                  {t.beslutsbrev.exampleTitle}
                </h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {t.beslutsbrev.examples.map((example, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      {example}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-sm text-muted-foreground text-center mb-4">
                {t.beslutsbrev.positioning}
              </p>

              <div className="text-center">
                <Link to="/verktyg?calc=fastighetsvarde">
                  <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                    {t.beslutsbrev.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* 7. Trust Architecture Section - "Byggt för orientering" */}
      {t.trustArchitecture && (
        <section className="max-w-3xl mx-auto" data-testid="trust-architecture-section">
          <div className="bg-forest-50 border border-forest-200 rounded-xl p-6 md:p-8">
            <h3 className="text-lg font-semibold text-forest-800 mb-4 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-forest-600" />
              {t.trustArchitecture.title}
            </h3>
            <ul className="space-y-3">
              {t.trustArchitecture.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-forest-700">
                  <CheckCircle2 className="h-4 w-4 text-forest-600 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* 8. Video Section - Optional, less prominent */}
      {t.video && (
        <section className="space-y-4 max-w-2xl mx-auto" data-testid="video-section">
          <div className="text-center">
            <p className="text-xs font-medium text-forest-600 uppercase tracking-wide mb-1">
              {t.video.label}
            </p>
            <p className="text-sm text-muted-foreground">
              {t.video.description}
            </p>
          </div>
          <div className="relative overflow-hidden rounded-xl bg-forest-900">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                ref={iframeRef}
                key={isMuted ? 'muted' : 'unmuted'}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/lTkzHSgRU84?mute=${isMuted ? 1 : 0}&loop=1&playlist=lTkzHSgRU84&controls=0&rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&disablekb=1&playsinline=1&origin=${window.location.origin}`}
                title="Skogskalkyl – Hur skogen utvecklas"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="absolute bottom-3 right-3 z-10">
              <Button
                onClick={toggleSound}
                variant="outline"
                size="sm"
                className="bg-black/60 border-white/30 text-white hover:bg-black/80 backdrop-blur-sm text-xs"
                data-testid="sound-toggle-btn"
              >
                {isMuted ? (
                  <>
                    <VolumeX className="mr-1 h-3 w-3" />
                    {t.video.soundOn}
                  </>
                ) : (
                  <>
                    <Volume2 className="mr-1 h-3 w-3" />
                    {t.video.soundOff}
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* 7. FAQ Section */}
      {t.faq && (
        <section className="max-w-3xl mx-auto" data-testid="faq-section">
          <h2 className="text-xl md:text-2xl font-bold text-forest-800 text-center mb-6">
            {t.faq.title}
          </h2>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50/50 transition-colors">
                <h3 className="font-semibold text-forest-800 mb-2">{item.q}</h3>
                <p className="text-sm text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 8. Final CTA Section — neutral wording in maintenance mode */}
      {!isAuthenticated && (
        <section className="bg-gradient-to-br from-forest-700 to-forest-800 rounded-2xl p-8 md:p-12 text-center text-white" data-testid="final-cta">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            {t.finalCta?.title || 'Börja med en beräkning'}
          </h2>
          <p className="text-forest-200 mb-8 max-w-xl mx-auto">
            {AUTH_ENABLED
              ? (t.finalCta?.description || 'Testa direkt – ingen inloggning krävs. Skapa konto senare om du vill spara dina resultat.')
              : 'Testa direkt i webbläsaren – ingen registrering krävs.'}
          </p>
          
          {/* Primary CTA - Test directly */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/verktyg">
              <Button size="lg" className="bg-white text-forest-700 hover:bg-forest-50 min-w-[200px]" data-testid="test-now-btn">
                <Calculator className="mr-2 h-5 w-5" />
                {t.finalCta?.primaryBtn || 'Testa nu'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            {/* Secondary CTA - Register (hidden in maintenance mode) */}
            {AUTH_ENABLED && (
              <Link to="/register">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 min-w-[200px]" data-testid="register-secondary-btn">
                  <UserPlus className="mr-2 h-5 w-5" />
                  {t.finalCta?.secondaryBtn || 'Skapa konto'}
                </Button>
              </Link>
            )}
          </div>
          
          {AUTH_ENABLED && (
            <p className="text-forest-300 text-sm mt-6">
              {t.finalCta?.note || 'Gratis att testa. Konto krävs endast för att spara.'}
            </p>
          )}
        </section>
      )}

      {/* 8. SEO Text Section */}
      <section className="max-w-3xl mx-auto" data-testid="seo-section">
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-6">
            <p className="text-sm text-slate-600 leading-relaxed">
              {t.seo.text}
            </p>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default Home;
